import * as React from 'react';
import { useEffect, useState } from 'react';
import { StructuredModal } from '../../../shared/components/modals/Modal';
import { Job, OUTCOME_OPTIONS, OutcomeType } from '../types/JobClosingSurvey';
import { apiClient } from '../../../shared/utils/apiClient';
import { CloseJobError, buildCloseJobError } from '../utils/closeJobError';

interface QuickCloseMenuProps {
  job: Job | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

const QuickCloseMenu: React.FC<QuickCloseMenuProps> = ({
  job,
  isOpen,
  onClose,
  onSuccess,
  onError
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState<OutcomeType | null>(null);

  // A refused close is shown inside the dialog, which stays open so the member
  // can read the reason and retry or cancel.
  const [closeError, setCloseError] = useState<CloseJobError | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCloseError(null);
    }
  }, [isOpen, job?.id]);

  const handleQuickClose = async (outcome: OutcomeType) => {
    if (isSubmitting || !job) return;

    setIsSubmitting(true);
    setSelectedOutcome(outcome);
    setCloseError(null);

    try {
      const response = await apiClient.post<{ success?: boolean; message?: string; error?: string }>(
        '/job_closing_surveys/quick_close',
        {
        job_id: job.id,
        outcome: outcome,
        outcome_other: outcome === 'other' ? 'Closed via quick close menu' : undefined
        }
      );

      if (response.ok && response.data?.success) {
        onSuccess?.(response.data.message || 'Job closed successfully');
        onClose();

        // Small delay before reload to show success message
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        const message = response.data?.error || response.error || 'Failed to close job';
        setCloseError(buildCloseJobError(message, response.status));
        onError?.(message);
      }
    } catch (error) {
      console.error('Error in quick close:', error);
      const errorMessage = error instanceof Error
        ? error.message
        : 'An error occurred while closing the job. Please try again.';
      setCloseError(buildCloseJobError(errorMessage));
      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
      setSelectedOutcome(null);
    }
  };

  // Icons for each outcome type
  const getOutcomeIcon = (outcome: OutcomeType): string => {
    switch (outcome) {
      case 'hired_through_platform':
        return '✅';
      case 'filled_another_source':
        return '🔄';
      case 'no_longer_hiring':
        return '⏸️';
      case 'no_qualified_candidates':
        return '🔍';
      case 'still_hiring':
        return '⏳';
      case 'other':
        return '📝';
      default:
        return '•';
    }
  };

  const footer = (
    <button
      type="button"
      className="btn btn-secondary"
      onClick={onClose}
      disabled={isSubmitting}
    >
      Cancel
    </button>
  );

  return (
    <StructuredModal
      show={isOpen}
      onHide={onClose}
      title="Close Job"
      centered
      footer={footer}
    >
      <p className="text-muted mb-4">
        Select a hiring outcome for <strong>"{job?.job_title || 'this job'}"</strong>:
      </p>

      {/* Server refused the close. Stay open so they can retry or cancel. */}
      {closeError && (
        <div className="alert alert-danger" role="alert" data-testid="close-job-error">
          <div>{closeError.message}</div>
          {closeError.help && (
            <div className="mt-2">{closeError.help}</div>
          )}
        </div>
      )}

      <div className="d-flex flex-column gap-2">
        {OUTCOME_OPTIONS.map((option) => {
          const isSelected = selectedOutcome === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleQuickClose(option.value)}
              disabled={isSubmitting}
              className="btn text-start d-flex align-items-center"
              style={{
                padding: '0.875rem 1rem',
                backgroundColor: isSelected ? '#176fc9' : '#f8f9fa',
                color: isSelected ? 'white' : '#212529',
                border: '1px solid #dee2e6',
                borderRadius: '0.5rem',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting && !isSelected ? 0.6 : 1,
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.currentTarget.style.backgroundColor = '#176fc9';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.borderColor = '#176fc9';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting && !isSelected) {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.color = '#212529';
                  e.currentTarget.style.borderColor = '#dee2e6';
                }
              }}
            >
              <span style={{
                fontSize: '1.25rem',
                marginRight: '0.875rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '1.75rem'
              }}>
                {getOutcomeIcon(option.value)}
              </span>
              <span style={{ flex: 1, fontWeight: 500 }}>
                {isSelected && isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Closing job...
                  </>
                ) : (
                  option.label
                )}
              </span>
            </button>
          );
        })}
      </div>
    </StructuredModal>
  );
};

export default QuickCloseMenu;
